import { WorkOrdersFieldsFragment } from "@/gql/graphql";
import { WorkOrderSupplierThread } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderSupplierThread";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Bell, Info, Truck, UserCheck, Users } from "lucide-react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { UpdateWorkOrderDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/UpdateWorkOrderDialog";
import { AssignAdminDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AssignAdminDialog";
import { UpdateWorkOrderNotificationSettingsDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/UpdateWorkOrderNotificationSettingsDialog";
import { formatDateTime } from "@/lib/Formatters/formatDateTime";
import { WorkOrderMap } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderMap";
import { Suspense } from "react";
import { useTranslations } from "use-intl";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import { ReturnTripBanner } from "@/app/ProDrivers/WorkOrders/Components/ReturnTripBanner";
import { WorkOrderNotes } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderNotes";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

export function WorkOrderDetailsCard({
  workOrder,
}: {
  workOrder: WorkOrdersFieldsFragment;
}) {
  const { open } = useDialog(UpdateWorkOrderDialog);
  const { open: openAssignAdmin } = useDialog(AssignAdminDialog);
  const { open: openNotificationSettings } = useDialog(
    UpdateWorkOrderNotificationSettingsDialog,
  );
  const { isAdmin } = useTenant();
  const tVehicle = useTranslations("vehicle");
  const t = useTranslations("workOrder.details");

  const assignAdminProps = {
    workOrderId: workOrder.id,
    assigneeIds: workOrder.adminAssignees.map((assignee) => assignee.id),
  };

  return (
    <Card className="overflow-hidden">
      <WorkOrderMap
        workOrder={workOrder}
        onEdit={() => {
          open({
            workOrderId: workOrder.id,
          });
        }}
      />

      <CardContent className="text-sm">
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          {/* Departure Location - matching booking card style */}
          <div className="relative border-l-4 border-blue-200 pl-6">
            <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
              <div className="flex size-5 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                A
              </div>
            </div>
            <div className="font-semibold">
              {workOrder.departureAddress.city || t("pickUpLocation")}
            </div>

            <div className="mt-1 flex items-center font-bold text-blue-600">
              <span>{formatDateTime(workOrder.depart_after)}</span>
            </div>

            <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
              <p>{workOrder.departureAddress.formatted_address_line_1}</p>
              {workOrder.departureAddress.formatted_address_line_2 && (
                <p>{workOrder.departureAddress.formatted_address_line_2}</p>
              )}
            </address>
          </div>

          {/* Delivery Location - matching booking card style */}
          <div className="relative border-l-4 border-green-200 pl-6">
            <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
              <div className="flex size-5 items-center justify-center rounded-full bg-green-500 text-xs font-bold text-white">
                B
              </div>
            </div>
            <div className="font-semibold">
              {workOrder.deliveryAddress.city || t("dropOffLocation")}
            </div>

            <div className="mt-1 flex items-center font-bold text-green-600">
              <span>{formatDateTime(workOrder.deliver_before)}</span>
            </div>

            <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
              <p>{workOrder.deliveryAddress.formatted_address_line_1}</p>
              {workOrder.deliveryAddress.formatted_address_line_2 && (
                <p>{workOrder.deliveryAddress.formatted_address_line_2}</p>
              )}
            </address>
          </div>
        </div>

        {workOrder.has_return_trip ? <ReturnTripBanner /> : null}

        <Separator className="my-4" />

        {/* Vehicle, Driver, Notifications and Assignees Row */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Vehicle Type */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
              <Truck className="h-3.5 w-3.5" /> {t("vehicleType")}
            </div>
            <div className="font-medium">
              {tVehicle(`type.${workOrder.vehicle_type}`)}
            </div>
          </div>

          {/* Number of Drivers */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
              <Users className="h-3.5 w-3.5" /> {t("driversRequired")}
            </div>
            <div className="font-medium">{workOrder.number_of_drivers}</div>
          </div>

          {/* Notifications */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
              <Bell className="h-3.5 w-3.5" /> {t("notifications")}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {workOrder.notificationSettings.length > 0 ? (
                workOrder.notificationSettings.map((setting) => (
                  <span
                    key={setting.id}
                    className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                  >
                    {setting.email}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">
                  {t("noNotificationRecipients")}
                </span>
              )}
              <button
                type="button"
                aria-label={t("manageNotifications")}
                title={t("manageNotifications")}
                className="rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                onClick={() =>
                  openNotificationSettings({ workOrderId: workOrder.id })
                }
              >
                <PlusIcon className="size-4" />
              </button>
            </div>
          </div>

          {/* Assignees */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
              <UserCheck className="h-3.5 w-3.5" /> {t("assignees")}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {workOrder.adminAssignees.length > 0 ? (
                workOrder.adminAssignees.map((assignee) => (
                  <span
                    key={assignee.id}
                    className="inline-flex items-center gap-1 text-xs text-gray-700"
                  >
                    <img
                      src="/imoova-logo.svg"
                      alt="Imoova"
                      className="size-4"
                    />
                    {assignee.name}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">
                  {t("noAssignees")}
                </span>
              )}
              {isAdmin ? (
                <button
                  type="button"
                  aria-label={t("assignAdmin")}
                  className="rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => openAssignAdmin(assignAdminProps)}
                >
                  <PlusIcon className="size-4" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Description Section - matching booking card inclusions style */}
        {workOrder.description && (
          <>
            <Separator className="my-4" />
            <div className="grid gap-3">
              <div className="flex items-center gap-1.5 font-medium text-gray-800">
                <Info className="h-4 w-4" /> {t("additionalInfo")}
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="whitespace-pre-wrap text-sm text-gray-700">
                  {workOrder.description}
                </p>
              </div>
            </div>
          </>
        )}

        <AdminSection containerClassName="mt-4">
          <WorkOrderNotes key={workOrder.id} workOrder={workOrder} />
        </AdminSection>

        <Separator className="my-4" />

        <Suspense>
          <WorkOrderSupplierThread workOrder={workOrder} />
        </Suspense>
      </CardContent>
    </Card>
  );
}
